import _, { add, get, update } from "lodash";
import React from "react";
import { computed, observable, ObservableSet } from "mobx";
import {
  registerRootStore,
  frozen,
  Frozen,
  getSnapshot,
  model,
  Model,
  modelAction,
  modelFlow,
  prop,
  _async,
  _await,
  idProp,
  computedTree,
  Ref,
} from "mobx-keystone";
import { notifications } from "@mantine/notifications";
import { createContext } from "react";
import { sendToRailsController } from "../helpers/requests";
import { validateInvoice } from "../helpers/stringValidation";
import {
  MIN_CHANNEL_SIZE_SATS,
  MODALS,
  NetworkType,
  UNDER_THIS_AMOUNT_MUST_USE_SEND_ALL,
} from "../helpers/constants";
import {
  highestOutboundChannelBalance,
  unclosedChannelsOpenedByUser,
} from "./cubby/channelInfo";
import {
  amountThatCouldBeUsedToOpenChannels,
  mustRetainInWalletToPayForChannelClosures,
} from "./cubby/ensureBalances";
import { sendFundsDirectiveAndWarning } from "./cubby/directivesAndWarnings";
import {
  outBoundChannelChoices,
  outBoundLiquidity,
  totalReserves,
} from "./cubby/sendOffchain";
import {
  hasPublicChannelsWithInboundLiquidityAndAtLeastSixConfirmations,
  inboundLiquidity,
  inboundLiquidityOfBiggestPrivateChannel,
  inboundLiquidityOfBiggestPublicChannel,
} from "./cubby/receiveOffchain";
import {
  isConsideredReadyForBasicLightningApiRequests,
  litRpcIsReady,
  shouldShowGetInfoButton,
} from "./cubby/lifecycleStatus";
import {
  addNewErrorAndUpdateCubbyErrors,
  updateCubbyErrors,
} from "./cubby/handleErrors";
import {
  getFeeEstimate,
  getFeeEstimateForArbitrarySatoshisAmount,
  handleOnchainFeeEstimateResponse,
  safeEstimatedOnchainFee,
  sixConfirmationEstimateOrNull,
} from "./cubby/feeEstimates";
import {
  handleKeysendPublicKeyChange,
  managePublicKeyValidationData,
} from "./cubby/keysend";
import {
  updateAddPeerSuccesses,
  updateCloseChannelSuccesses,
  updateOnchainPaymentSuccesses,
  updateOpenChannelSuccesses,
} from "./cubby/updateCubby";
import {
  waitForBasicLightningApiRequests,
  waitForOnchainActions,
} from "../helpers/onStartup";
import { updateBoltElevenUserInvoices } from "../helpers/boltElevenInvoices";
import { Task } from "./Task";
import { updateTasks } from "../helpers/updateTasks";
import { Blocker } from "./Blocker";
import {
  handleChannelOfferLspError,
  handleChannelOfferSelection,
  resetChannelOffers,
} from "../helpers/channelOffers";
import { makeBlockerOrReturnFalse } from "../helpers/blockers";
import {
  updateInvoiceUpdateEvents,
  updatePayOffchainEvents,
} from "../helpers/events";
import { payToLightningAddressInvoice } from "../helpers/paytoLightningAddress";
import { NostrConnection, nostrConnectionRef } from "./NostrConnection";
import { customAlphabet } from "nanoid";
import { ledgerEntries } from "../helpers/ledger";
import { currentlyFocusedLedgerItemJson } from "../helpers/currentlyFocusedLedgerItem";

const nanoid = customAlphabet("1234567890abcdef", 10);
@model("exrails/Cubby")
export class Cubby extends Model({
  id: idProp,
  hasLoaded: prop<boolean>(false),
  // Account fields
  accountUuid: prop<string>(""),
  has2Fa: prop<Frozen<boolean> | undefined>(),

  // Cubby fields
  // Cubby fields
  userEmail: prop<string>(""),
  cubbyNetwork: prop<NetworkType>("mainnet"), // Default to 'mainnet'
  cubbyUuid: prop<string>(""),
  runStatus: prop<string>(""),
  chainBalance: prop<number | string>("pending"),
  pendingChainBalance: prop<number | string>("pending"),
  cubbyErrors: prop<Frozen<any[]> | undefined>(),
  cubbyEvents: prop<Frozen<any[]> | undefined>(),
  onchainAddresses: prop<Frozen<any[]> | undefined>(),
  onchainTransactions: prop<Frozen<any[]> | undefined>(),
  channelEvents: prop<Frozen<any[]> | undefined>(),
  peerEvents: prop<Frozen<any[]> | undefined>(),
  peerLists: prop<Frozen<any[]> | undefined>(),
  openChannelSuccesses: prop<Frozen<any[]> | undefined>(),
  closeChannelSuccesses: prop<Frozen<any[]> | undefined>(),
  cubbyInfo: prop<Frozen<any> | undefined>(),
  addPeerSuccesses: prop<Frozen<any[]> | undefined>(),
  channelsList: prop<Frozen<any[]> | undefined>(),
  onchainPaymentSuccesses: prop<Frozen<any[]> | undefined>(),
  lastOnchainFeeEstimates: prop<Frozen<any[]> | undefined>(),
  lastDecodedPayRequest: prop<Frozen<any> | undefined>(),
  alias: prop<string | null>(null),
  invoiceUpdateEvents: prop<Frozen<any[]> | undefined>(),
  payOffchainEvents: prop<Frozen<any[]> | undefined>(),
  isAdvancedMode: prop<boolean>(false),
  isAdminButEverythingServerProtected: prop<boolean>(false),
  publicKey: prop<string | null>(null),
  environmentData: prop<Frozen<any> | undefined>(),
  useUsdForFiat: prop<boolean>(true),
  useSatoshisForBtc: prop<boolean>(true),
  userBoltElevenInvoices: prop<Frozen<any[]> | undefined>(),
  tasks: prop<Task[]>(() => []),
  blockers: prop<Blocker[]>(() => []),
  nostrConnections: prop<NostrConnection[]>(() => []),
  selectedNostrConnectionRef: prop<Ref<NostrConnection> | undefined>(undefined),
  channelOffers: prop<Frozen<any> | null>(null),
  remainingDaysOfGuaranteedUptime: prop<number | null>(null),
  lightning_address: prop<string | null>(null),
  number_of_additional_lightning_address_changes_allowed: prop<number | null>(
    null
  ),
  stagedLightningAddressDataForPayment: prop<Frozen<any> | undefined>(),
  stagedUserInputForNodeSignature: prop<string | null>(null),
  messageThatWasSignedByNodeText: prop<string | null>(null),
  recommended_lsp_node_public_key: prop<string | null>(null),
  nostr_public_key: prop<string | null>(null),
  use_web_analytics: prop<boolean>(false),
  last_recovery_attempt: prop<number | null>(null),
  two_fa_interval_minutes: prop<number>(1440),
}) {
  @observable
  lastProposedSatoshiSendAmount = 0;

  @observable
  lastGotFeeEstimate = 0;

  @observable
  feeEstimateTimeoutId: NodeJS.Timeout | null = null;

  @observable
  lastProposedLightningInvoiceForPayment: string = "";

  @observable
  satoshisAmountForOpenChannel = 150_000;

  @observable
  shouldShow2FAFormForPayments = false;

  @observable
  lastProposedPublicKeyForKeysend: string = "";

  @observable
  lastProposedPublicKeyForKeysendIsValid: boolean = false;

  @observable
  lastKeysendErrormessage: string = "";

  @observable
  currentModal: string | null = null;

  @observable
  currentlyFocusedChannelTransactionIdForChannelInfo: string | null = null;

  @observable
  currentlyFocusedChannelTransactionIdForChannelClosure: string | null = null;

  @observable
  shouldShowPeerNumberIndicator = false;

  @observable
  lastProposedBolt11InvoiceUuid: string | null = null;

  @observable
  lastBolt11UserGeneratedPayRequest: string | null = null;

  @observable
  mostRecentTaskServerPushedAt: number = 0;

  @observable
  channelOfferPendingAnInvoiceUuid: string | null = null;

  @observable
  channelOfferInvoiceForUserPayment: string | null = null;

  @observable
  currentlyFocusedArticleSlug: string | null = null;

  @observable
  userHasGoneIdle: boolean = false;

  @observable
  nostrPrivateKey: string | null = null;

  @observable
  numTransactionsToShow = 6;

  @observable
  twoFaIntervalErrorMessage: string | null = null;

  @observable
  currentlyFocused: string | null = null;

  @observable
  currentlyFocusedLedgerItemId: string | null = null;

  @observable
  payToLightningAddressInvoiceInPaymentProgress: string | null = null;

  @modelAction
  setPayToLightningAddressInvoiceInPaymentProgress(newInvoice: string | null) {
    this.payToLightningAddressInvoiceInPaymentProgress = newInvoice;
  }

  @computed
  get currentlyFocusedLedgerItemJson() {
    return currentlyFocusedLedgerItemJson(this);
  }

  @modelAction
  setCurrentlyFocusedLedgerItemId(newId: string | null) {
    this.currentlyFocusedLedgerItemId = newId;
  }

  @modelAction
  setTwoFaIntervalMinutes(newInterval: number) {
    this.two_fa_interval_minutes = newInterval;
  }

  @modelAction
  setTwoFaIntervalErrorMessage(newMessage: string | null) {
    this.twoFaIntervalErrorMessage = newMessage;
  }

  @modelAction
  setNumTransactionsToShow(newNum: number) {
    this.numTransactionsToShow = newNum;
    localStorage.setItem("numToShow", newNum.toString());
  }

  @modelAction
  setNostrPrivateKey(newPrivateKey: string | null) {
    this.nostrPrivateKey = newPrivateKey;
  }

  @modelAction
  setUserHasGoneIdle(newIdleStatus: boolean) {
    this.userHasGoneIdle = newIdleStatus;
  }

  @modelAction
  setCurrentlyFocusedArticleSlug(slug: string | null) {
    this.currentlyFocusedArticleSlug = slug;
  }

  @computed
  get hasMoreThanOneReceiveOnlyNostrConnection() {
    return (
      this.nostrConnections.filter(
        (connection) => connection.security_level_name === "receive_only"
      ).length > 1
    );
  }

  @computed
  get nostrConnectionsOrderedByCreatedAtDesc() {
    return this.nostrConnections.slice().sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateB - dateA;
    });
  }

  @modelAction
  refreshNostrConnections() {
    this.selectNostrConnection(undefined);
    this.nostrConnections = [];
    this.cubbyFastInfo("get_all_nostr_connections");
  }

  @modelAction
  instantiateNostrConnections(data: any[]) {
    this.nostrConnections = data.map((nostrConnectionData) => {
      return new NostrConnection({
        _version: 0,
        created_at: nostrConnectionData.created_at,
        uuid: nostrConnectionData.uuid,
        name: nostrConnectionData.name,
        security_level_name: nostrConnectionData.security_level_name,
        budget_amount_sats: nostrConnectionData.budget_amount_sats,
        total_amount_left_in_budget:
          nostrConnectionData.total_amount_left_in_budget,
        budget_renewal_period_days:
          nostrConnectionData.budget_renewal_period_days,
        connection_expiration_days:
          nostrConnectionData.connection_expiration_days,
        budget_last_renewed: nostrConnectionData.budget_last_renewed,
        nwc_uri: nostrConnectionData.nwc_uri,
        connection_expiration_measured_from_date:
          nostrConnectionData.connection_expiration_measured_from_date,
      });
    });
  }

  @computed
  get selectedNostrConnection() {
    return this.selectedNostrConnectionRef
      ? this.selectedNostrConnectionRef.current
      : undefined;
  }
  @modelAction
  selectNostrConnection(nostrConnection: NostrConnection | undefined) {
    if (nostrConnection && !this.nostrConnections.includes(nostrConnection))
      throw new Error("unknown nostrconnection");
    this.selectedNostrConnectionRef = nostrConnection
      ? nostrConnectionRef(nostrConnection)
      : undefined;
  }

  @modelAction
  setMessageThatWasSignedByNodeText(data: string | null) {
    this.messageThatWasSignedByNodeText = data;
  }

  @modelAction
  handleLightningAddressChangeSuccess(newFrontOfLightningAddress: string) {
    this.lightning_address =
      newFrontOfLightningAddress + "@" + this.environmentData.data.base_domain;
    this.number_of_additional_lightning_address_changes_allowed =
      this.number_of_additional_lightning_address_changes_allowed - 1;
  }

  @modelAction
  setStagedUserInputForNodeSignature(data: string | null) {
    this.stagedUserInputForNodeSignature = data;
  }

  @modelAction
  payToLightningAddressInvoice(
    unValidatedPayRequest: string,
    intendedSatoshisAmount: number,
    hTagMustMatch: string,
    twoFACode: string
  ) {
    payToLightningAddressInvoice(
      this,
      unValidatedPayRequest,
      intendedSatoshisAmount,
      hTagMustMatch,
      twoFACode
    );
  }

  @modelAction
  setStagedLightningAddressDataForPayment(data: any) {
    console.log("setting staged lightning address data for payment", data);
    this.stagedLightningAddressDataForPayment = frozen(data);
  }

  @computed
  get lspPublicKeyIfConnectedOrNull() {
    const recommendedPublicKeyForChannelOpen =
      this.recommended_lsp_node_public_key;

    const foundPeer = this.peerLists.data.find(
      (peer) => peer.public_key === recommendedPublicKeyForChannelOpen
    );

    return foundPeer?.public_key || null;
  }

  @computed
  get canReceiveLightningPayments() {
    if (!this.isConsideredReadyForBasicLightningApiRequests) {
      return false;
    }
    if (this.inboundLiquidity < 1) {
      return false;
    }
    if (this.inboundLiquidityOfBiggestPrivateChannel > 0) {
      return true;
    }
    const hasWithAtLeastSix =
      hasPublicChannelsWithInboundLiquidityAndAtLeastSixConfirmations(this);
    if (hasWithAtLeastSix) {
      return true;
    }
    return false;
  }

  @computed
  get shouldShowStartNodeButton() {
    return this.runStatus === "initialized" || this.runStatus === "stopped";
  }

  @modelAction
  setChannelOfferInvoiceForUserPayment(newInvoice: string | null) {
    this.channelOfferInvoiceForUserPayment = newInvoice;
  }

  @modelAction
  setChannelOfferPendingAnInvoiceUuid(newUuid: string | null) {
    this.channelOfferPendingAnInvoiceUuid = newUuid;
  }

  @modelAction
  handleChannelOfferLspInvoiceSuccess(data: any) {
    console.log("handling channel offer lsp invoice success", data);
    if (this.channelOfferPendingAnInvoiceUuid == data.channelOfferUuid) {
      this.setChannelOfferInvoiceForUserPayment(data.payRequest);
    }
  }

  @modelAction
  resetChannelOffers(uuid) {
    resetChannelOffers(this, uuid);
  }

  @modelAction
  handleChannelOfferLspError(data: any) {
    handleChannelOfferLspError(this, data);
  }

  @modelAction
  handleChannelOfferSelection(amount: number, isPrivate: boolean) {
    handleChannelOfferSelection(this, amount, isPrivate);
  }

  @modelAction
  setChannelOffers(newOffers: any) {
    this.channelOffers = frozen(newOffers);
  }

  @modelAction
  updateOnchainTransactions(newOnchainTransactions: any[]) {
    this.onchainTransactions = frozen(newOnchainTransactions);
    console.log("updated onchain transactions", newOnchainTransactions);
  }

  @modelAction
  updateMostRecentTaskServerPushedAt(newTimestamp: number) {
    this.mostRecentTaskServerPushedAt = newTimestamp;
  }

  @modelAction
  makeBlockerOrReturnFalse(kind: string, uniqueExternalIdentifier: string) {
    return makeBlockerOrReturnFalse(this, kind, uniqueExternalIdentifier);
  }

  @computed
  get tasksToShowToUser() {
    return this.tasks
      .filter((task) => !task.frontEndSawTerminalLifecycleStatus)
      .sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp)
      .slice(0, 3); // Limit to max 3 tasks
  }

  @modelAction
  updateTasks(newTasks: any[]) {
    updateTasks(this, newTasks);
  }

  @modelAction
  updateBoltElevenUserInvoices(newInvoices: any[]) {
    updateBoltElevenUserInvoices(this, newInvoices);
  }

  @modelAction
  resetUserGeneratedBolt11PayRequest() {
    this.setLastBolt11UserGeneratedPayRequest(null);
    this.setLastProposedBolt11InvoiceUuid(null);
  }

  @modelAction
  setLastProposedBolt11InvoiceUuid(newUuid: string) {
    this.lastProposedBolt11InvoiceUuid = newUuid;
  }

  @modelAction
  setLastBolt11UserGeneratedPayRequest(newPayRequest: string) {
    this.lastBolt11UserGeneratedPayRequest = newPayRequest;
  }

  @modelAction
  setShouldShowPeerNumberIndicator(newShouldShow: boolean) {
    this.shouldShowPeerNumberIndicator = newShouldShow;
  }

  @computed
  get currentlyFocusedChannelDataForChannelClosure() {
    return this.channelsList?.data.find(
      (channel) =>
        channel.channel_point ===
        this.currentlyFocusedChannelTransactionIdForChannelClosure
    );
  }

  @modelAction
  setCurrentlyFocusedChannelTransactionIdForChannelClosure(
    channel: any | null
  ) {
    if (!channel) {
      this.currentlyFocusedChannelTransactionIdForChannelClosure = null;
      return;
    }
    this.currentlyFocusedChannelTransactionIdForChannelClosure =
      channel.channel_point;
    console.log(
      "just set",
      this.currentlyFocusedChannelTransactionIdForChannelClosure
    );
  }

  @computed
  get currentlyFocusedChannelDataForChannelInfo() {
    return this.channelsList?.data.find(
      (channel) =>
        channel.channel_point ===
        this.currentlyFocusedChannelTransactionIdForChannelInfo
    );
  }

  @modelAction
  setCurrentlyFocusedChannelTransactionIdForChannelInfo(channel: any | null) {
    console.log(channel);
    if (!channel) {
      this.currentlyFocusedChannelTransactionIdForChannelInfo = null;
      return;
    }
    this.currentlyFocusedChannelTransactionIdForChannelInfo =
      channel.channel_point;
  }

  @modelAction
  setModal(modalName: string) {
    this.currentModal = modalName;
    // console.log("setModal", modalName);
  }

  @modelAction
  handleAdvancedModeChange() {
    this.isAdvancedMode = !this.isAdvancedMode;
    this.cubbyLife("set_advanced_mode", {
      isAdvancedMode: this.isAdvancedMode,
    });
  }

  @modelAction
  handleUseUsdForFiatChange(newMode: boolean) {
    this.useUsdForFiat = newMode;
    this.cubbyLife("set_use_usd_for_fiat", { useUsd: newMode });
  }

  @modelAction
  handleUseSatoshisForBtcChange(newMode: boolean) {
    this.useSatoshisForBtc = newMode;
    this.cubbyLife("set_use_satoshis_for_btc", { useSatoshis: newMode });
  }

  @modelAction
  managePublicKeyValidationData(data: any) {
    managePublicKeyValidationData(this, data);
  }

  @modelAction
  handleKeysendPublicKeyChange(newValue: string) {
    handleKeysendPublicKeyChange(this, newValue);
  }

  @modelAction
  clearLightningInvoiceStringForPayment() {
    this.lastProposedLightningInvoiceForPayment = "";
    this.lastDecodedPayRequest = undefined;
  }

  @modelAction
  updatePayOffchainEvents(newPayOffchainEvents: any[]) {
    updatePayOffchainEvents(this, newPayOffchainEvents);
  }

  @modelAction
  updateInvoiceUpdateEvents(newInvoiceUpdateEvents: any[]) {
    updateInvoiceUpdateEvents(this, newInvoiceUpdateEvents);
  }

  @modelAction
  updateSatoshisAmountForOpenChannel(newAmount: number) {
    this.satoshisAmountForOpenChannel = newAmount;
  }

  @modelAction
  updateDecodedPayRequest(data: any) {
    console.log("updatedDecodedPayRequest", data);
    const invoice = data.inputInvoice;
    if (invoice != this.lastProposedLightningInvoiceForPayment) {
      console.log("invoice did not match last proposed invoice, ignoring");
      return;
    }

    this.lastDecodedPayRequest = frozen(data.decodedPayRequest);
  }

  @modelAction
  setInvoiceForPayment(newInvoice: string) {
    this.lastDecodedPayRequest = undefined;
    this.lastProposedLightningInvoiceForPayment = newInvoice;
    if (validateInvoice(newInvoice)) {
      this.cubbyLife("decode_pay_request", {
        invoice: newInvoice,
      });
    }
  }

  @modelAction
  setAlias(newAlias: string) {
    this.alias = newAlias;
    this.cubbyLife("set_alias", { alias: newAlias });
  }

  /// FEE ESTIMATES

  @modelAction
  getFeeEstimateForArbitrarySatoshisAmount(amountInSatoshis: number) {
    getFeeEstimateForArbitrarySatoshisAmount(this, amountInSatoshis);
  }

  @modelAction
  getFeeEstimate(amountInSatoshis: number, destinationAddress: string) {
    getFeeEstimate(this, amountInSatoshis, destinationAddress);
  }

  @computed
  get sixConfirmationEstimateOrNull() {
    return sixConfirmationEstimateOrNull(this);
  }

  @computed
  get safeEstimatedOnchainFee() {
    return safeEstimatedOnchainFee(this);
  }

  @computed
  get safeEstimatedClosureCost() {
    return safeEstimatedOnchainFee(this);
  }

  @computed
  get hasUnclosedChannels() {
    return this.channelsList?.data.some(
      (channel) => channel.synthetic_status !== "closed"
    );
  }

  @computed
  get hasOpenChannels() {
    return this.channelsList?.data.some(
      (channel) => channel.synthetic_status === "open"
    );
  }

  @computed
  get hasOpenPrivateChannels() {
    return this.channelsList?.data.some(
      (channel) => channel.synthetic_status === "open" && channel.is_private
    );
  }
  @computed
  get hasOpenPublicChannels() {
    return this.channelsList?.data.some(
      (channel) =>
        channel.synthetic_status === "open" && channel.is_private == false
    );
  }

  @computed
  get outboundLiquidity(): number {
    return outBoundLiquidity(this);
  }

  @computed
  get totalReserves(): number {
    return totalReserves(this);
  }

  @computed
  get inboundLiquidity(): number {
    return inboundLiquidity(this);
  }

  @computed
  get inboundLiquidityOfBiggestPrivateChannel(): number {
    return inboundLiquidityOfBiggestPrivateChannel(this);
  }

  @computed
  get inboundLiquidityOfBiggestPublicChannel(): number {
    return inboundLiquidityOfBiggestPublicChannel(this);
  }

  @computed
  get invoiceShouldDefaultToPrivate(): boolean {
    if (
      this.inboundLiquidityOfBiggestPublicChannel >
      this.inboundLiquidityOfBiggestPrivateChannel
    ) {
      return false;
    }
    return true;
  }

  @computed
  get isAllowedToDestroy() {
    if (this.cubbyNetwork == "testnet") {
      return true;
    }

    if (this.totalOfAllBalances > 3_000) {
      return false;
    }

    if (this.hasUnclosedChannels) {
      return false;
    }
    return true;
  }

  @computed
  get closedChannels() {
    return this.channelsList.data.filter(
      (channel) => channel.synthetic_status === "closed"
    );
  }

  @computed
  get unclosedChannelsOpenedByUser() {
    return unclosedChannelsOpenedByUser(this);
  }

  @computed
  get canUseSendAll() {
    return this.numberOfUnclosedChannelsOpenedByUser == 0;
  }

  @computed
  get numberOfUnclosedChannelsOpenedByUser() {
    return this.unclosedChannelsOpenedByUser?.length || 0;
  }

  @computed
  get isRequiredToUse2Fa() {
    return this.has2Fa?.data === true || this.safeChainBalance > 100_000;
  }

  @modelAction
  resetLastOnchainFeeEstimates() {
    this.lastOnchainFeeEstimates = frozen([]);
  }

  @modelAction
  handleOnchainFeeEstimateResponse(
    amountInSatoshis: number,
    feesInSatoshis: any[]
  ) {
    handleOnchainFeeEstimateResponse(this, amountInSatoshis, feesInSatoshis);
  }

  @modelAction
  updateOnchainPaymentSuccesses(newOnchainPaymentSuccesses: any) {
    updateOnchainPaymentSuccesses(this, newOnchainPaymentSuccesses);
  }

  @modelAction
  updateCloseChannelSuccesses(newCloseChannelSuccesses: any) {
    updateCloseChannelSuccesses(this, newCloseChannelSuccesses);
  }

  @modelAction
  updateOpenChannelSuccesses(newOpenChannelSuccesses: any) {
    updateOpenChannelSuccesses(this, newOpenChannelSuccesses);
  }

  @modelAction
  updateChannels(channelsList: any) {
    this.channelsList = frozen(channelsList);
  }

  @modelAction
  updatePeerLists(newPeerLists: any) {
    if (
      this.peerLists.data &&
      this.peerLists.data.length < newPeerLists.length
    ) {
      this.shouldShowPeerNumberIndicator = true;
    }
    this.peerLists = frozen(newPeerLists);
    // console.log("peer-lists", this.peerLists.data);
  }

  @modelAction
  updateAddPeerSuccesses(newAddPeerSuccesses: any) {
    updateAddPeerSuccesses(this, newAddPeerSuccesses);
  }
  @modelAction
  addNewErrorAndUpdateCubbyErrors(description: string) {
    addNewErrorAndUpdateCubbyErrors(this, description);
  }

  @modelAction
  updateCubbyErrors(newCubbyErrors: any) {
    updateCubbyErrors(this, newCubbyErrors);
  }

  @modelAction
  updateOnchainBalances(newOnchainBalances: any) {
    this.chainBalance = newOnchainBalances.chain_balance;
    this.pendingChainBalance = newOnchainBalances.pending_chain_balance;
  }

  @modelAction
  updateOnchainAddresses(newOnchainAddresses: any) {
    this.onchainAddresses = frozen(newOnchainAddresses);
  }

  @modelAction
  setHasLoaded(hasLoaded: boolean) {
    this.hasLoaded = hasLoaded;
  }
  @modelAction
  updateRunStatus(newRunStatus: string) {
    this.runStatus = newRunStatus;
  }
  @modelAction
  updateInfo(newInfo: any) {
    this.cubbyInfo = frozen(newInfo);
  }

  @computed
  get allowOnchainActions() {
    if (!this.cubbyInfo.data) {
      return false;
    }
    return this.cubbyInfo.data.onchain_only_ready == true;
  }

  @computed
  get litRpcIsReady() {
    return litRpcIsReady(this);
  }

  @computed
  get isConsideredReadyForBasicLightningApiRequests() {
    return isConsideredReadyForBasicLightningApiRequests(this);
  }

  @computed
  get shouldShowGetInfoButton() {
    return shouldShowGetInfoButton(this);
  }

  @modelAction
  hydrateDataFromPage() {
    const rootDiv = document.getElementById("root");
    const json = JSON.parse(rootDiv.dataset.hydrate);
    //console.log("json from page", json);
    // Hydrate account fields
    this.accountUuid = json.account.accountUuid;
    this.has2Fa = frozen(json.account.has2Fa);
    this.isAdvancedMode = json.account.is_advanced_mode;

    // Hydrate cubby fields
    this.cubbyUuid = json.cubby.uuid;
    this.runStatus = json.cubby.run_status;
    this.cubbyNetwork = json.cubby.network;
    this.chainBalance = json.cubby.chain_balance;
    this.pendingChainBalance = json.cubby.pending_chain_balance;
    this.cubbyErrors = frozen(json.cubby.cubby_errors);
    this.cubbyEvents = frozen(json.cubby.cubby_events);
    this.onchainAddresses = frozen(json.cubby.onchain_addresses);
    this.onchainTransactions = frozen(json.cubby.onchain_transactions);
    this.channelEvents = frozen(json.cubby.channel_events);
    this.peerEvents = frozen(json.cubby.peer_events);
    this.peerLists = frozen(json.cubby.peer_lists);
    this.openChannelSuccesses = frozen(json.cubby.open_channel_successes);
    this.cubbyInfo = frozen(json.cubby.cubby_info);
    this.addPeerSuccesses = frozen(json.cubby.add_peer_successes);
    this.channelsList = frozen(json.cubby.channels_list);
    this.onchainPaymentSuccesses = frozen(json.cubby.send_to_chain_successes);
    this.alias = json.cubby.alias;
    this.publicKey = json.cubby.public_key;
    this.invoiceUpdateEvents = frozen(json.cubby.invoice_update_events);
    this.payOffchainEvents = frozen(json.cubby.pay_offchain_events);
    this.isAdminButEverythingServerProtected = json.account.is_admin;
    this.useSatoshisForBtc = json.account.use_satoshis_for_btc;
    this.userEmail = json.account.email;
    this.useUsdForFiat = json.account.use_usd_for_fiat;
    this.environmentData = frozen(json.environment);
    this.userBoltElevenInvoices = frozen(json.cubby.user_bolt_eleven_invoices);
    this.remainingDaysOfGuaranteedUptime =
      json.cubby.remaining_days_of_guaranteed_uptime;
    this.lightning_address = json.cubby.lightning_address;
    this.number_of_additional_lightning_address_changes_allowed =
      json.cubby.number_of_additional_lightning_address_changes_allowed;
    this.recommended_lsp_node_public_key =
      json.cubby.recommended_lsp_node_public_key;
    this.nostr_public_key = json.cubby.nostr_public_key;
    this.use_web_analytics = json.account.use_web_analytics;
    this.last_recovery_attempt = json.cubby.last_recovery_attempt;
    this.nostrConnections = [];
    this.selectNostrConnection(null);
    this.two_fa_interval_minutes = json.account.two_fa_interval_minutes;
    this.runOnStartup();
  }

  @modelFlow
  runOnStartup = _async(function* (this: Cubby) {
    console.log("in run on startup");
    this.setHasLoaded(true);
    this.cubbyFastInfo("get_tasks");
    if (this.runStatus != "stopped") {
      console.log("runstatus is not stopped so get info");
      this.cubbyLife("get_info");
    }
    if (this.runStatus != "subscriptions_started") {
      console.log('runStatus is not "subscriptions_started", so return');
      return;
    }

    ///wait for allowOnchainActions to be true
    yield* _await(waitForOnchainActions(this));

    // After allowOnchainActions becomes true, check for isConsideredReadyForBasicLightningApiRequests
    if (this.allowOnchainActions) {
      yield* _await(waitForBasicLightningApiRequests(this));
    }
  });

  @modelFlow
  cubbyLife = _async(function* (
    this: Cubby,
    directive: string,
    data: any = {}
  ) {
    const message = {
      cubbyUuid: this.cubbyUuid,
      directive: directive,
      data: data,
    };
    sendToRailsController(message, "/cubby_life/cubby_life")
      .then((response) => {
        //console.log(response);
        if (response.error) {
          console.log("about to make a new error");
          console.log("here is the error", response.error);
          if (response.error == "Invalid 2FA code") {
            notifications.show({
              color: "red",
              title: "Invalid 2FA code",
              message: "Please enter a valid 2FA code",
            });
          }

          this.addNewErrorAndUpdateCubbyErrors(response.error);
        }
        if (response.notification) {
          notifications.show({
            color: "blue",
            title: "Notification",
            message: response.notification,
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  });

  @modelFlow
  cubbyFastInfo = _async(function* (
    this: Cubby,
    directive: string,
    data: any = {}
  ) {
    const message = {
      cubbyUuid: this.cubbyUuid,
      directive: directive,
      data: data,
    };
    sendToRailsController(message, "/cubby_life/cubby_fast_info")
      .then((response) => {
        //console.log(response);
        if (response.error) {
          console.log("about to make a new error");
          this.addNewErrorAndUpdateCubbyErrors(response.error);
        }
        console.log("fast info:", response);
        if ("does_need_2FA_form" in response) {
          this.setDoesNeed2FAFormForPayments(response.does_need_2FA_form);
        }
        if ("publicKeyValidationData" in response) {
          this.managePublicKeyValidationData(response.publicKeyValidationData);
        }
        if ("tasks" in response) {
          this.updateTasks(response.tasks);
        }
        if ("channelOffer" in response) {
          console.log("setting channel offers to", response.channelOffer);
          this.setChannelOffers(response.channelOffer);
        }
        if ("nostrConnections" in response) {
          console.log("here empty and rebuild nostr connections");
          this.instantiateNostrConnections(response.nostrConnections);
        }
        if ("nostrPrivateKey" in response) {
          this.setNostrPrivateKey(response.nostrPrivateKey);
        }
        if (
          "kind" in response &&
          response["kind"] == "change_to_2fa_interval"
        ) {
          console.log(
            "manage this response in cubby fast info change_to_2fa_interval",
            response
          );
          if (response?.success == false) {
            this.setTwoFaIntervalErrorMessage(response.message);
          } else {
            console.log("success is true, so set the interval");
            this.setTwoFaIntervalMinutes(response.new_interval_minutes);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  });

  @modelFlow
  cubbyFastUpdate = _async(function* (
    this: Cubby,
    directive: string,
    data: any = {}
  ) {
    const message = {
      cubbyUuid: this.cubbyUuid,
      directive: directive,
      data: data,
    };
    sendToRailsController(message, "/cubby_life/cubby_fast_update")
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  });

  @modelAction
  setDoesNeed2FAFormForPayments(doesNeed2FAForm: boolean) {
    this.shouldShow2FAFormForPayments = doesNeed2FAForm;
    console.log(
      "shouldShow2FAFormForPayments",
      this.shouldShow2FAFormForPayments
    );
  }

  @modelAction
  outBoundChannelChoices(proposedAmount: number) {
    return outBoundChannelChoices(this, proposedAmount);
  }

  @computed
  get highestOutboundChannelBalance(): number {
    return highestOutboundChannelBalance(this);
  }

  /// balances logic

  @computed
  get isAllowedToSendFunds() {
    {
      return this.allowOnchainActions && this.safeChainBalance > 0;
    }
  }

  @computed
  get totalOfAllBalances() {
    return this.safeChainBalance + this.safePendingChainBalance;
  }

  @computed get safePendingChainBalance() {
    return typeof this.pendingChainBalance === "string"
      ? 0
      : this.pendingChainBalance;
  }

  @computed
  get safeChainBalance() {
    if (typeof this.chainBalance === "string") {
      return 0;
    }
    return this.chainBalance;
  }

  @computed
  get underThisAmountMustUseSendAll() {
    if (this.sixConfirmationEstimateOrNull) {
      return Math.max(
        UNDER_THIS_AMOUNT_MUST_USE_SEND_ALL,
        this.sixConfirmationEstimateOrNull.fee * 3
      );
    }
    return UNDER_THIS_AMOUNT_MUST_USE_SEND_ALL;
  }

  @computed
  get amountThatCouldBeSentWithoutSendAll() {
    return Math.max(
      this.safeChainBalance -
        (this.underThisAmountMustUseSendAll +
          this.mustRetainInWalletToPayForChannelClosures),
      0
    );
  }

  @computed
  get mustRetainInWalletToPayForChannelClosures() {
    return mustRetainInWalletToPayForChannelClosures(this);
  }

  @computed
  get minimumOnchainFundsToOpenChannel() {
    return this.safeEstimatedClosureCost * 2 + MIN_CHANNEL_SIZE_SATS * 1.25;
  }

  @computed
  get amountThatCouldBeUsedToOpenChannels() {
    return amountThatCouldBeUsedToOpenChannels(this);
  }

  @computed
  get hasEnoughFundsOnchainToOpenChannel(): boolean {
    return this.amountThatCouldBeUsedToOpenChannels >= MIN_CHANNEL_SIZE_SATS;
  }

  @computed
  get sendFundsDirectiveAndWarning(): any {
    return sendFundsDirectiveAndWarning(this);
  }

  @computed
  get hasSomeHistory() {
    return (
      this.onchainTransactions?.data.length > 0 ||
      this.payOffchainEvents?.data.length > 0 ||
      this.invoiceUpdateEvents?.data.length > 0 ||
      this.onchainPaymentSuccesses?.data.length > 0 ||
      this.cubbyErrors?.data.length > 0
    );
  }

  @computed
  get shouldShowForceStopButton() {
    return (
      this.isAdminButEverythingServerProtected ||
      this.runStatus === "shutting_down"
    );
  }
  @computed
  get ledgerEntries() {
    return ledgerEntries(this, this.numTransactionsToShow);
  }
}

export const runCreateCubby = () => {
  const cubby = new Cubby({
    accountUuid: "",
    has2Fa: undefined,
    cubbyUuid: "",
    runStatus: "",
    chainBalance: 0,
    pendingChainBalance: 0,
    cubbyErrors: frozen([]),
    cubbyEvents: frozen([]),
    onchainAddresses: frozen([]),
    onchainTransactions: frozen([]),
    channelEvents: frozen([]),
    peerEvents: frozen([]),
    peerLists: frozen([]),
    openChannelSuccesses: frozen([]),
    cubbyInfo: frozen({}),
    addPeerSuccesses: frozen([]),
    channelsList: frozen([{}]),
    closeChannelSuccesses: frozen([]),
    onchainPaymentSuccesses: frozen([]),
    lastOnchainFeeEstimates: frozen([]),
    lastDecodedPayRequest: undefined,
    invoiceUpdateEvents: frozen([]),
    payOffchainEvents: frozen([]),
    environmentData: frozen({}),
    userBoltElevenInvoices: frozen([]),
    stagedLightningAddressDataForPayment: frozen({}),
  });

  registerRootStore(cubby);
  return cubby;
};

const createdCubby = runCreateCubby();
//console.log("created workspace", getSnapshot(createdWorkspace));
export const CubbyContext = createContext<Cubby>(createdCubby);

export interface CubbyInterface {
  Cubby: Cubby;
}
